import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { setTokenProvider } from '@/lib/api'
import { api } from '@/lib/api'
import { env, isAllowedEmail } from '@/lib/env'
import { applyProfileLanguage } from '@/lib/i18n'
import { isApiError } from '@/lib/problem'
import {
  beginMockSignIn,
  forgetMockSession,
  isMockSignedOut,
  mockLanguage,
  mockRoles,
  rememberMockLanguage,
  takeMockLoginRequest,
} from './mockSession'
import type { Role, UiLanguage, UserProfile } from '@/lib/types'

export type AuthStatus =
  | 'initialising'
  | 'signed-out'
  | 'signing-in'
  | 'signed-in'
  /** Authenticated with Microsoft, but not an account the library accepts. */
  | 'not-permitted'
  | 'error'

interface AuthState {
  status: AuthStatus
  profile: UserProfile | null
  /** The email we know about even when the profile call was rejected. */
  email: string
  error: unknown
  /** Set when a silent token renewal failed; the shell shows the dialog (§4.3). */
  sessionExpired: boolean
}

interface AuthContextValue extends AuthState {
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  setPreferredLanguage: (language: UiLanguage) => Promise<void>
  dismissSessionExpired: () => void
  hasRole: (role: Role) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

const MOCK_PROFILE: UserProfile = {
  oid: '00000000-0000-0000-0000-0000000000aa',
  email: 'aziz.karimov@itpu.uz',
  displayName: 'Aziz Karimov',
  // The mock account carries every role so the whole UI is reachable in
  // development, including the teacher identity choice and the admin console.
  roles: ['user', 'teacher', 'admin'],
  preferredLanguage: 'en',
  isBlocked: false,
  createdAt: '2026-02-01T08:00:00Z',
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'initialising',
    profile: null,
    email: '',
    error: null,
    sessionExpired: false,
  })

  const loadProfile = useCallback(async (email: string) => {
    try {
      const { data } = await api.me()
      if (data.isBlocked) {
        setState({
          status: 'not-permitted',
          profile: data,
          email: data.email,
          error: null,
          sessionExpired: false,
        })
        return
      }
      await applyProfileLanguage(data.preferredLanguage)
      setState({
        status: 'signed-in',
        profile: data,
        email: data.email,
        error: null,
        sessionExpired: false,
      })
    } catch (error) {
      if (isApiError(error) && (error.isForbidden || error.isAccountBlocked)) {
        setState({ status: 'not-permitted', profile: null, email, error, sessionExpired: false })
        return
      }
      setState({ status: 'error', profile: null, email, error, sessionExpired: false })
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function bootstrap(): Promise<void> {
      if (env.authMode === 'mock') {
        if (isMockSignedOut()) {
          if (!cancelled) setState((prev) => ({ ...prev, status: 'signed-out' }))
          return
        }
        setTokenProvider(async () => 'mock-token')

        // The same thing `navigateToLoginRequestUrl` does for real: the sign-in
        // began on another page, and the browser has to be put back on it
        // before anything renders.
        const loginRequest = takeMockLoginRequest()
        if (loginRequest && loginRequest !== `${window.location.pathname}${window.location.search}`) {
          window.location.assign(loginRequest)
          return
        }

        if (cancelled) return
        const language = mockLanguage(MOCK_PROFILE.preferredLanguage)
        await applyProfileLanguage(language)
        setState({
          status: 'signed-in',
          profile: {
            ...MOCK_PROFILE,
            roles: mockRoles(MOCK_PROFILE.roles),
            preferredLanguage: language,
          },
          email: MOCK_PROFILE.email,
          error: null,
          sessionExpired: false,
        })
        return
      }

      const { acquireToken, accountEmail, initializeMsal, msalInstance } = await import('./msal')
      try {
        await initializeMsal()
      } catch (error) {
        if (!cancelled) {
          setState({ status: 'error', profile: null, email: '', error, sessionExpired: false })
        }
        return
      }

      setTokenProvider(async () => {
        const token = await acquireToken()
        if (!token) setState((prev) => ({ ...prev, sessionExpired: true }))
        return token
      })

      const account = msalInstance.getActiveAccount()
      if (!account) {
        if (!cancelled) setState((prev) => ({ ...prev, status: 'signed-out' }))
        return
      }

      const email = accountEmail(account)
      // Defence in depth; the single-tenant registration is the real gate (§4.1).
      if (!isAllowedEmail(email)) {
        if (!cancelled) {
          setState({ status: 'not-permitted', profile: null, email, error: null, sessionExpired: false })
        }
        return
      }

      if (!cancelled) await loadProfile(email)
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [loadProfile])

  const signIn = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'signing-in', error: null }))
    if (env.authMode === 'mock') {
      beginMockSignIn()
      return
    }
    const { apiRequest, msalInstance } = await import('./msal')
    try {
      // A redirect, not a popup: it survives pop-up blockers and returns the
      // user to the deep link they originally asked for (FR-1.4).
      await msalInstance.loginRedirect(apiRequest)
    } catch (error) {
      setState((prev) => ({ ...prev, status: 'error', error }))
    }
  }, [])

  const signOut = useCallback(async () => {
    if (env.authMode === 'mock') {
      forgetMockSession()
      setState({ status: 'signed-out', profile: null, email: '', error: null, sessionExpired: false })
      return
    }
    const { msalInstance } = await import('./msal')
    await msalInstance.logoutRedirect()
  }, [])

  const refreshProfile = useCallback(async () => {
    await loadProfile(state.email)
  }, [loadProfile, state.email])

  const setPreferredLanguage = useCallback(async (language: UiLanguage) => {
    setState((prev) =>
      prev.profile ? { ...prev, profile: { ...prev.profile, preferredLanguage: language } } : prev,
    )
    if (env.authMode === 'mock') {
      rememberMockLanguage(language)
      return
    }
    try {
      await api.updateMe({ preferredLanguage: language })
    } catch {
      // The language is already applied locally; a failed save simply means it
      // will not follow the user to their next device (FR-7.4).
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn,
      signOut,
      refreshProfile,
      setPreferredLanguage,
      dismissSessionExpired: () => setState((prev) => ({ ...prev, sessionExpired: false })),
      hasRole: (role: Role) => state.profile?.roles.includes(role) ?? false,
    }),
    [state, signIn, signOut, refreshProfile, setPreferredLanguage],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}

export function useCurrentUser(): UserProfile | null {
  return useAuth().profile
}
